import { useEffect, useRef } from 'react';
import { AutoSizer } from 'react-virtualized';
import { useMutation } from '@apollo/client';
import {
  IAnalysisInfoMutation,
  IAnalysisInfoMutationVariables,
  QueryType,
} from '../../../../utils/apollo/graphqlSchema';
import { CHANGE_ANALYSIS_INFO } from '../../../../utils/apollo/analysis';
import printGraphqlErrors from '../../../../utils/apollo/printGraphqlErrors';
import { assertNotNil } from '../../../../utils/types';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { renameAnalysisVisit } from '../../../../redux/slices/analysesVisits';
import { analysesVisitsSelector } from '../../../../redux/selectors/analysesVisits';
import { IExecutingAnalysisInfo } from '../../../../redux/slices/executingAnalyses';
import ChartExecutionProgress from './ChartExecutionProgress';
import CancelQuery from './CancelQuery';
import useMinChartHeight from '../../utils/useMinChartHeight';
import { ChartMode } from '../../types';
import * as ChartUI from '../../ChartUI';
import KubitLogoLoader from '../KubitLogoLoader';
import { LoadingChartHeader } from '../../ChartHeader';

interface IProps {
  analysisId: string;
  executionInfo: IExecutingAnalysisInfo;
}

const mode = ChartMode.Build;

const ExecutingChartLoader = ({ analysisId, executionInfo }: IProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();

  // Because report data is still loading, we will try to get data from history
  const analysesVisitsList = useAppSelector(analysesVisitsSelector);
  const analysisFromHistory = analysesVisitsList?.find(
    (analysisVisit) => analysisVisit.id === analysisId,
  );
  const analysisName = analysisFromHistory?.display ?? '';
  const analysisDescription = analysisFromHistory?.description ?? '';

  const minChartHeight = useMinChartHeight(ChartMode.Build);
  const queryType = analysisFromHistory?.queryType;

  const [changeAnalysisInfo] = useMutation<
    IAnalysisInfoMutation,
    IAnalysisInfoMutationVariables
  >(CHANGE_ANALYSIS_INFO);

  const handleChangeReportName = async (newName: string) => {
    const { data } = await changeAnalysisInfo({
      variables: {
        input: {
          analysisId,
          display: newName,
          description: analysisDescription,
        },
      },
    });

    const errors = data?.analysisInfo?.errors;

    if (printGraphqlErrors(errors)) {
      return;
    }

    assertNotNil(data?.analysisInfo?.entity);

    dispatch(
      renameAnalysisVisit({
        analysisId,
        analysisName: newName,
      }),
    );
  };

  useEffect(() => {
    // Autoscroll when execution started
    const timeoutId = setTimeout(() => {
      if (ref.current) {
        ref.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 300);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  return (
    <ChartUI.Container ref={ref}>
      <LoadingChartHeader
        mode={mode}
        chartTitle={analysisName}
        updateChartTitle={
          queryType !== QueryType.CohortSample
            ? handleChangeReportName
            : undefined
        }
      />
      <ChartUI.ChartMainWrapper $mode={mode}>
        <div style={{ minHeight: minChartHeight }}>
          <AutoSizer disableWidth={true}>
            {({ height }) => (
              <KubitLogoLoader height={height}>
                <ChartExecutionProgress executionInfo={executionInfo} />
                <CancelQuery analysisId={analysisId} />
              </KubitLogoLoader>
            )}
          </AutoSizer>
        </div>
      </ChartUI.ChartMainWrapper>
    </ChartUI.Container>
  );
};

export default ExecutingChartLoader;
